/**
 * Utility functions for generating context messages based on PTO suggestions context
 */

import { addDays, format } from 'date-fns'
import type { IntlShape } from 'react-intl'
import { PTOSuggestionsContextInfo, ICompellingReason, CompellingReasonType } from '@vacationtracker/shared/types/user-insight'
import { PTOSuggestionsResponse } from '../../../types'

/**
 * Gets the check-back date message for recent return scenarios
 */
export function getCheckBackDate(ptoSuggestions: PTOSuggestionsResponse): string | null {
  if (!ptoSuggestions.contextInfo) {
    return null
  }

  const { type, meta } = ptoSuggestions.contextInfo
  const today = new Date()

  switch (type) {
    case PTOSuggestionsContextInfo.RECENT_RETURN: {
      const daysSinceReturn = meta?.daysSinceReturn || 0
      const thresholdDays = meta?.recentLeaveThresholdDays || 14
      const remainingDays = thresholdDays - daysSinceReturn
      if (remainingDays > 0) {
        const checkBackDate = addDays(today, remainingDays)
        return format(checkBackDate, 'MMM d')
      }
      return null
    }
    case PTOSuggestionsContextInfo.UPCOMING_LEAVE:
      // For upcoming leave, we don't show a specific date, just the message
      return null
    default:
      return null
  }
}

/**
 * Gets the check-back message based on context type
 */
export function getCheckBackMessage(ptoSuggestions: PTOSuggestionsResponse, formatMessage: IntlShape['formatMessage']): string | null {
  if (!ptoSuggestions.contextInfo) {
    return null
  }

  const { type } = ptoSuggestions.contextInfo

  if (type === PTOSuggestionsContextInfo.UPCOMING_LEAVE) {
    return formatMessage({ id: 'energyLevel.recommendationsPaused.checkBackAfterReturn' })
  }

  const checkBackDate = getCheckBackDate(ptoSuggestions)
  if (checkBackDate) {
    return formatMessage({ id: 'energyLevel.recommendationsPaused.checkBack' }, { date: checkBackDate })
  }

  return null
}

/**
 * Gets paused message title and description based on context type
 */
export function getPausedMessage(ptoSuggestions: PTOSuggestionsResponse, formatMessage: IntlShape['formatMessage']): { title: string; description: string } | null {
  if (!ptoSuggestions.contextInfo) {
    return null
  }

  const { type } = ptoSuggestions.contextInfo

  switch (type) {
    case PTOSuggestionsContextInfo.RECENT_RETURN:
      return {
        title: formatMessage({ id: 'energyLevel.recommendationsPaused.title' }),
        description: formatMessage({ id: 'energyLevel.recommendationsPaused.recentReturn' }),
      }
    case PTOSuggestionsContextInfo.UPCOMING_LEAVE:
      return {
        title: formatMessage({ id: 'energyLevel.recommendationsPaused.upcomingLeaveTitle' }),
        description: formatMessage({ id: 'energyLevel.recommendationsPaused.upcomingLeave' }),
      }
    case PTOSuggestionsContextInfo.NO_COMPELLING_REASON:
      return {
        title: formatMessage({ id: 'energyLevel.recommendationsPaused.title' }),
        description: formatMessage({ id: 'energyLevel.recommendationsPaused.noCompellingReason' }),
      }
    case PTOSuggestionsContextInfo.REQUIREMENTS_NOT_MET:
      return {
        title: formatMessage({ id: 'energyLevel.recommendationsPaused.title' }),
        description: formatMessage({ id: 'energyLevel.recommendationsPaused.requirementsNotMet' }),
      }
    case PTOSuggestionsContextInfo.INSUFFICIENT_BALANCE:
      return {
        title: formatMessage({ id: 'energyLevel.recommendationsPaused.title' }),
        description: formatMessage({ id: 'energyLevel.recommendationsPaused.insufficientBalance' }),
      }
    default:
      return null
  }
}

/**
 * Translates compelling reason to localized message
 */
export function translateCompellingReason(reason: ICompellingReason, formatMessage: IntlShape['formatMessage']): string {
  switch (reason.type) {
    case CompellingReasonType.EXPIRING_PTO:
      return formatMessage({ id: 'energyLevel.compellingReason.expiringPto' }, { expiringAmount: reason.meta?.expiringAmount || 0 })
    case CompellingReasonType.UPCOMING_HOLIDAYS: {
      const holidayCount = reason.meta?.holidayCount || 0
      return formatMessage({ id: 'energyLevel.compellingReason.upcomingHolidays' }, { holidayCount, plural: holidayCount !== 1 ? 's' : '' })
    }
    case CompellingReasonType.MAJOR_TEAM_GAP:
      return formatMessage({ id: 'energyLevel.compellingReason.majorTeamGap' })
    case CompellingReasonType.GOOD_TEAM_AVAILABILITY:
      return formatMessage({ id: 'energyLevel.compellingReason.goodTeamAvailability' })
    case CompellingReasonType.PLANNER_RECHARGE_TYPE:
      return formatMessage({ id: 'energyLevel.compellingReason.plannerRechargeType' })
    default:
      return ''
  }
}
